import React, { useRef, useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'src/shared/components/ui/tooltip';

export type TemporalSegment = {
    start: number; // ms timestamp
    end: number | null; // null = ongoing
    color: string; // Tailwind bg class
    tooltip: string;
};

export type TemporalRow = {
    label: string;
    segments: TemporalSegment[];
    showAddButton?: boolean;
};

type TemporalChartProps = {
    rows: TemporalRow[];
    onBarClick?: (rowLabel: string, segmentIndex: number) => void;
    onAddClick?: (rowLabel: string) => void;
};

const MIN_MONTH_PX = 80;
const MIN_MONTHS = 6;

const TemporalChart = ({ rows, onBarClick, onAddClick }: TemporalChartProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        if (!scrollRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });
        observer.observe(scrollRef.current);
        setContainerWidth(scrollRef.current.clientWidth);
        return () => observer.disconnect();
    }, []);

    if (rows.length === 0) {
        return <p className='text-sm text-grey-70 italic'>No timeline data available.</p>;
    }

    const now = Date.now();
    let minTime = Infinity;
    let maxTime = -Infinity;

    for (const row of rows) {
        for (const seg of row.segments) {
            if (seg.start < minTime) minTime = seg.start;
            const segEnd = seg.end ?? now;
            if (segEnd > maxTime) maxTime = segEnd;
        }
    }

    // Snap to month boundaries
    const startDate = new Date(minTime);
    startDate.setDate(1);
    startDate.setHours(0, 0, 0, 0);
    const chartStartMs = startDate.getTime();

    const endDate = new Date(maxTime);
    endDate.setMonth(endDate.getMonth() + 2);
    endDate.setDate(1);
    endDate.setHours(0, 0, 0, 0);
    const chartEndMs = endDate.getTime();

    const rawMonths = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
    const totalMonths = Math.max(rawMonths, MIN_MONTHS);
    if (rawMonths < MIN_MONTHS) {
        endDate.setMonth(startDate.getMonth() + MIN_MONTHS);
    }
    const chartEndMsAdjusted = endDate.getTime();

    // Month width: expand to fill container, but never below MIN_MONTH_PX
    const monthWidthPx = containerWidth > 0 ? Math.max(containerWidth / totalMonths, MIN_MONTH_PX) : MIN_MONTH_PX;
    const chartWidthPx = totalMonths * monthWidthPx;

    const effectiveEnd = rawMonths < MIN_MONTHS ? chartEndMsAdjusted : chartEndMs;
    const toPx = (time: number) => ((time - chartStartMs) / (effectiveEnd - chartStartMs)) * chartWidthPx;

    const formatMonth = (date: Date) => date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });

    // Generate month grid labels
    const monthLabels: { label: string; leftPx: number }[] = [];
    const cursor = new Date(startDate);
    for (let i = 0; i < totalMonths; i++) {
        monthLabels.push({ label: formatMonth(cursor), leftPx: i * monthWidthPx });
        cursor.setMonth(cursor.getMonth() + 1);
    }

    const renderBars = (row: TemporalRow, rowIdx: number) => {
        const MIN_SEG_PX = 12;
        const GAP_PX = 2;
        const sorted = [...row.segments].map((seg, origIdx) => ({ seg, origIdx })).sort((a, b) => a.seg.start - b.seg.start);

        let layoutCursor = 0;
        const layouts = sorted.map(({ seg, origIdx }, i) => {
            const naturalLeft = toPx(seg.start);
            const naturalRight = seg.end === null ? chartWidthPx : toPx(seg.end);
            const adjustedLeft = Math.max(naturalLeft, layoutCursor);
            const naturalWidth = naturalRight - adjustedLeft;
            const width = seg.end === null ? Math.max(chartWidthPx - adjustedLeft, MIN_SEG_PX) : Math.max(naturalWidth, MIN_SEG_PX);
            layoutCursor = adjustedLeft + width + GAP_PX;
            return { seg, origIdx, left: adjustedLeft, width, sortIdx: i };
        });

        const isClickable = Boolean(onBarClick);

        return layouts.map(({ seg, origIdx, left, width, sortIdx }) => {
            const startLabel = formatMonth(new Date(seg.start));
            const endLabel = seg.end === null ? 'Present' : formatMonth(new Date(seg.end));
            const opacity = sortIdx % 2 === 0 ? 1 : 0.75;
            return (
                <Tooltip key={origIdx} delayDuration={200}>
                    <TooltipTrigger asChild>
                        <div
                            data-testid={`temporal-bar-${rowIdx}-${origIdx}`}
                            className={`absolute top-0 h-full rounded ${seg.color} ${
                                isClickable ? 'cursor-pointer hover:brightness-110 transition-all' : ''
                            }`}
                            style={{
                                left,
                                width,
                                opacity,
                                zIndex: 2,
                                borderLeft: sortIdx > 0 ? '2px solid rgba(255,255,255,0.8)' : undefined,
                            }}
                            onClick={isClickable ? () => onBarClick!(row.label, origIdx) : undefined}
                        />
                    </TooltipTrigger>
                    <TooltipContent side='top'>
                        <div className='flex flex-col gap-0.5'>
                            <span className='font-medium'>{seg.tooltip}</span>
                            <span className='text-xs text-muted-foreground'>
                                {startLabel} → {endLabel}
                            </span>
                        </div>
                    </TooltipContent>
                </Tooltip>
            );
        });
    };

    return (
        <TooltipProvider>
            <div data-testid='temporal-chart' className='flex gap-2'>
                {/* Fixed left column: row labels */}
                <div className='flex flex-col gap-2 shrink-0'>
                    {rows.map(row => (
                        <div key={row.label} className='h-6 flex items-center'>
                            <span className='text-xs font-medium text-grey-70 w-20 text-right'>{row.label}</span>
                        </div>
                    ))}
                    <div className='h-4' /> {/* spacer for month labels row */}
                </div>

                {/* Scrollable middle: all chart rows scroll together */}
                <div ref={scrollRef} className='overflow-x-auto flex-1 min-w-0'>
                    <div style={{ width: chartWidthPx }} className='flex flex-col gap-2'>
                        {rows.map((row, rowIdx) => (
                            <div key={row.label} className='relative h-6 bg-grey-10 rounded'>
                                {monthLabels.map((m, i) => (
                                    <div key={i} className='absolute top-0 h-full border-l border-grey-20' style={{ left: m.leftPx }} />
                                ))}
                                {renderBars(row, rowIdx)}
                            </div>
                        ))}
                        {/* Month labels */}
                        <div className='flex'>
                            {monthLabels.map((m, i) => (
                                <div key={i} className='text-[10px] text-grey-50' style={{ width: monthWidthPx }}>
                                    {m.label}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Fixed right column: add buttons */}
                {onAddClick && (
                    <div className='flex flex-col gap-2 shrink-0'>
                        {rows.map(row => (
                            <div key={row.label} className='h-6 flex items-center'>
                                {row.showAddButton !== false ? (
                                    <button
                                        data-testid={`timeline-add-${row.label}`}
                                        onClick={() => onAddClick(row.label)}
                                        className='w-6 h-6 flex items-center justify-center rounded hover:bg-grey-20 transition-colors text-grey-50 hover:text-primary'
                                        title={`Add ${row.label.toLowerCase()}`}
                                    >
                                        <Plus className='w-3.5 h-3.5' />
                                    </button>
                                ) : (
                                    <div className='w-6' />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </TooltipProvider>
    );
};

export default TemporalChart;
